import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { api } from './client'

/**
 * CVs domain. The upload mutation lives in `uploads.ts` because it
 * needs axios for progress events; everything else stays on fetch.
 */

export interface CV {
  id: string
  fileName: string
  filePath: string
  type: string
  language?: string
  resumeText?: string
  createdAt: string
  updatedAt: string
}

export interface CVPage {
  items: CV[]
  total: number
  page: number
  perPage: number
}

export const cvsKeys = {
  all: ['cvs'] as const,
  list: (page: number, perPage: number) => [...cvsKeys.all, 'list', { page, perPage }] as const,
  detail: (id: string) => [...cvsKeys.all, 'detail', id] as const,
}

export const cvsListQuery = (page = 1, perPage = 50) =>
  queryOptions({
    queryKey: cvsKeys.list(page, perPage),
    queryFn: () => api.get<CVPage>(`/cvs?page=${page}&perPage=${perPage}`),
  })

export const cvQuery = (id: string) =>
  queryOptions({
    queryKey: cvsKeys.detail(id),
    queryFn: () => api.get<CV>(`/cvs/${id}`),
    enabled: id.length > 0,
  })

export function useDeleteCVMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (cvId: string) => api.delete<void>(`/cvs/${cvId}`),
    onSuccess: () => invalidateCVs(qc),
  })
}

export function invalidateCVs(qc: QueryClient) {
  return qc.invalidateQueries({ queryKey: cvsKeys.all })
}
